import { type Header, type Table, flexRender } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { ColumnFilter } from "./column-filter";
import { columnName } from "./column-name";

const ALIGN = { start: "justify-start", end: "justify-end", center: "justify-center" } as const;

/**
 * Jadval sarlavhasi: nom + filtr + saralash.
 *
 * Filtr jadval tepasidagi panelda emas, aynan o'z ustunida: "bu maydon qaysi
 * ustunga tegishli?" degan savol tug'ilmaydi, faol filtr esa belgining
 * rangidan va nuqtasidan ko'rinib turadi.
 */
export function DataTableHeader<TData>({ table }: { table: Table<TData> }) {
  const { t } = useTranslation();

  const sortLabel = (direction: false | "asc" | "desc", column: string) => {
    if (direction === "asc") return t("table.sortedAsc", { column });
    if (direction === "desc") return t("table.sortedDesc", { column });
    return t("table.sortBy", { column });
  };

  const filterButton = (header: Header<TData, unknown>) => {
    const value = header.column.getFilterValue();
    const active = value !== undefined && value !== "";
    const name = columnName(header.column);
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("table.filterBy", { column: name })}
            aria-pressed={active}
            className={cn(
              "relative grid size-6 place-items-center rounded-md transition-colors outline-none",
              "focus-visible:ring-ring/50 focus-visible:ring-2",
              active
                ? "bg-primary/12 text-primary"
                : "text-muted-foreground/70 hover:bg-foreground/5 hover:text-foreground",
            )}
          >
            <ListFilter className="size-3.5" />
            {active ? (
              <span className="bg-primary animate-pulse-ring text-primary absolute -top-0.5 -right-0.5 size-1.5 rounded-full" />
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto overflow-hidden rounded-xl p-0">
          <ColumnFilter column={header.column} label={name} />
        </PopoverContent>
      </Popover>
    );
  };

  const sortButton = (header: Header<TData, unknown>) => {
    const direction = header.column.getIsSorted();
    return (
      <button
        type="button"
        aria-label={sortLabel(direction, columnName(header.column))}
        onClick={header.column.getToggleSortingHandler()}
        className="text-muted-foreground/70 hover:text-foreground grid size-6 place-items-center rounded-md"
      >
        {direction === "asc" ? <ArrowUp className="text-primary size-3.5" /> : null}
        {direction === "desc" ? <ArrowDown className="text-primary size-3.5" /> : null}
        {!direction ? <ChevronsUpDown className="size-3.5" /> : null}
      </button>
    );
  };

  return (
    <thead>
      {table.getHeaderGroups().map(group => (
        <tr key={group.id}>
          {group.headers.map(header => {
            const meta = header.column.columnDef.meta;
            const direction = header.column.getIsSorted();
            return (
              <th
                key={header.id}
                scope="col"
                aria-sort={
                  direction === "asc"
                    ? "ascending"
                    : direction === "desc"
                      ? "descending"
                      : undefined
                }
                className={cn(
                  // Noshaffof fon: yopishgan sarlavha ostidan qatorlar ko'rinmasin.
                  "bg-muted text-muted-foreground sticky top-0 z-10 h-10 px-3 text-left text-[11px] font-semibold tracking-wider whitespace-nowrap uppercase",
                  "first:rounded-l-xl last:rounded-r-xl",
                )}
              >
                <div className={cn("flex items-center gap-1", ALIGN[meta?.align ?? "start"])}>
                  <span className="min-w-0 truncate">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </span>
                  {header.column.getCanFilter() && meta?.filterVariant
                    ? filterButton(header)
                    : null}
                  {header.column.getCanSort() ? sortButton(header) : null}
                </div>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}
